/**
 * ADR-0210 Slice 2 — one agent's page: trend, journey (page kinds), what
 * it asked for, what was decided, where it came from, and the products
 * it touched. Everything here is DERIVED (daily rollups), never signed
 * records; the page says so and links to the Mandates view for the
 * signed activity.
 */

import { ArrowLeft, ArrowRight, ChevronDown, ChevronRight } from 'lucide-react';
import { useContext, useState } from 'react';

import { SiteCtx } from '../../app/providers.js';
import { siteIsWordPress } from '../mandates/visit-identity.js';

import {
  agentIdentityStatus,
  impersonationHitsFromSignals,
  agentIdentityStatusExplanation,
  agentIdentityStatusLabel,
  isProbePath,
  looksLikeKnownBotName,
  trustLabelHeadline,
} from '@sill/types';
import type { AgentProductRollup, AgentRollupSummary, MerchantAgentDisputeRef, MerchantAgentVerdict } from '@sill/types/api';

import { CountryFlag } from '../../components/atoms/CountryFlag.js';
import { HoverBars } from '../../components/atoms/HoverBars.js';
import { Tooltip } from '../../components/atoms/Tooltip.js';
import { Pill } from '../../components/atoms/Pill.js';
import { TrustChip } from './trust/TrustChip.js';
import { useC } from '../../lib/theme.js';
import {
  CATEGORY_LABEL,
  DECISION_LABEL,
  PAGE_KIND_LABEL,
  PROTOCOL_LABEL,
  VERDICT_LABEL,
  agentDisplayName,
  fmtDay,
  fmtInt,
  fmtMoney,
  fmtPct,
  monoFont,
  sansFont,
  serifFont,
  sortedEntries,
  sumValues,
} from './format.js';

const JOURNEY_ORDER = ['product', 'collection', 'search', 'cart', 'checkout', 'other'] as const;

function Kicker({ children }: { children: string }): JSX.Element {
  const C = useC();
  return (
    <div style={{ fontFamily: monoFont, fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: C.muted, marginBottom: 10 }}>
      {children}
    </div>
  );
}

function Tile({ label, value, sub }: { label: string; value: string; sub?: string | undefined }): JSX.Element {
  const C = useC();
  return (
    <div style={{ padding: '14px 16px', border: `1px solid ${C.border}`, borderRadius: 2, background: C.surface }}>
      <div style={{ fontFamily: monoFont, fontSize: 9, letterSpacing: 1.5, textTransform: 'uppercase', color: C.muted }}>{label}</div>
      <div style={{ fontFamily: monoFont, fontSize: 22, fontWeight: 600, color: C.text, marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      {sub !== undefined ? <div style={{ fontFamily: sansFont, fontSize: 11, color: C.muted, marginTop: 2 }}>{sub}</div> : null}
    </div>
  );
}

/** Daily traffic (faint) with actions (solid); hover for the day's numbers. */
export function TrendChart({ daily }: { daily: AgentRollupSummary['daily'] }): JSX.Element {
  const first = daily[0];
  const last = daily[daily.length - 1];
  return (
    <HoverBars
      fluid
      width={640}
      height={120}
      aria-label="Daily agent traffic"
      axis={first !== undefined && last !== undefined ? { first: fmtDay(first.day), last: fmtDay(last.day) } : undefined}
      bars={daily.map((d) => ({
        value: d.page_loads,
        solid: d.actions,
        tooltip: [
          fmtDay(d.day),
          `${fmtInt(d.page_loads)} page loads · ${fmtInt(d.sessions)} sessions`,
          ...(d.actions > 0 ? [`${fmtInt(d.actions)} actions · ${fmtInt(d.approved)} approved · ${fmtInt(d.rejected)} refused`] : []),
        ],
      }))}
    />
  );
}

function CountList({ entries, labels, total, renderLabel }: { entries: Array<[string, number]>; labels?: Readonly<Record<string, string>>; total?: number; renderLabel?: (k: string) => React.ReactNode }): JSX.Element {
  const C = useC();
  if (entries.length === 0) return <div style={{ fontFamily: sansFont, fontSize: 12, color: C.dim }}>None recorded for this visitor in the window.</div>;
  const denom = total ?? entries.reduce((s, [, n]) => s + n, 0);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {entries.map(([k, n]) => {
        const pct = denom === 0 ? 0 : Math.round((n / denom) * 100);
        return (
          <Tooltip key={k} content={`${fmtInt(n)} · ${pct}% of ${fmtInt(denom)}`} displayMode="block" side="top">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, alignItems: 'center', cursor: 'default' }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontFamily: sansFont, fontSize: 12.5, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{renderLabel !== undefined ? renderLabel(k) : (labels?.[k] ?? k)}</div>
                <div style={{ height: 3, background: C.surface2, borderRadius: 2, marginTop: 3 }}>
                  <div style={{ height: 3, width: `${Math.max(pct, n > 0 ? 2 : 0)}%`, background: C.muted, borderRadius: 2 }} />
                </div>
              </div>
              <div style={{ fontFamily: monoFont, fontSize: 12, color: C.text, fontVariantNumeric: 'tabular-nums' }}>{fmtInt(n)}</div>
            </div>
          </Tooltip>
        );
      })}
    </div>
  );
}

function Section({
  title,
  help,
  children,
  collapsible = false,
  defaultOpen = true,
}: {
  title: string;
  /** One-line explanation shown right under the kicker. Omit for self-explanatory sections. */
  help?: string;
  children: React.ReactNode;
  collapsible?: boolean;
  defaultOpen?: boolean;
}): JSX.Element {
  const C = useC();
  const [open, setOpen] = useState(defaultOpen);
  if (!collapsible) {
    return (
      <div style={{ padding: 16, border: `1px solid ${C.border}`, borderRadius: 2, background: C.surface }}>
        <Kicker>{title}</Kicker>
        {help !== undefined ? (
          <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, marginTop: -4, marginBottom: 10, lineHeight: 1.4 }}>{help}</div>
        ) : null}
        {children}
      </div>
    );
  }
  return (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: 2, background: C.surface }}>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="w-full"
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 16px', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left',
          color: C.text,
        }}
      >
        <div>
          <Kicker>{title}</Kicker>
          {help !== undefined ? (
            <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, marginTop: -4, lineHeight: 1.4 }}>{help}</div>
          ) : null}
        </div>
        {open ? <ChevronDown size={14} color={C.muted} /> : <ChevronRight size={14} color={C.muted} />}
      </button>
      {open ? (
        <div style={{ padding: '0 16px 16px 16px', borderTop: `1px solid ${C.borderSoft}` }}>
          <div style={{ paddingTop: 12 }}>{children}</div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * `TopPagesExpanded` — the agent's top page paths, always open, 10 rows
 * default + "Show all N". Probe paths are filtered on the server, so the
 * list is clean by construction.
 */
function TopPagesExpanded({ paths }: { readonly paths: readonly { readonly path: string; readonly n: number }[] }): JSX.Element {
  const C = useC();
  const [all, setAll] = useState(false);
  const shown = all ? paths : paths.slice(0, 10);
  return (
    <div style={{ padding: 16, border: `1px solid ${C.border}`, borderRadius: 2, background: C.surface }}>
      <Kicker>Top pages</Kicker>
      <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, marginTop: -4, marginBottom: 10, lineHeight: 1.4 }}>
        Pages this agent loaded most often. Probe requests are listed separately below.
      </div>
      <CountList entries={shown.map((p) => [p.path, p.n])} />
      {paths.length > 10 ? (
        <button
          type="button"
          onClick={() => setAll((v) => !v)}
          style={{
            marginTop: 10,
            background: 'transparent',
            border: 'none',
            padding: 0,
            color: C.text,
            fontFamily: monoFont,
            fontSize: 11,
            textDecoration: 'underline',
            textDecorationColor: C.dim,
            textUnderlineOffset: '3px',
            cursor: 'pointer',
          }}
        >
          {all ? 'Show top 10' : `Show all ${paths.length}`}
        </button>
      ) : null}
    </div>
  );
}

/**
 * `ProbePathsSection` — the agent's probe paths, expanded. Shows Path /
 * Kind / Hits columns. First-/last-seen + response status would need an
 * API extension; today the table is honest about what it has.
 */
function ProbePathsSection({
  probes,
  name,
  total,
}: {
  readonly probes: ReadonlyArray<{ readonly path: string; readonly n: number }>;
  readonly name: string;
  readonly total: number;
}): JSX.Element {
  const C = useC();
  if (probes.length === 0) {
    return (
      <div style={{ padding: 16, border: `1px solid ${C.border}`, borderRadius: 2, background: C.surface }}>
        <Kicker>Probe paths</Kicker>
        <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted }}>
          {fmtInt(total)} probe {total === 1 ? 'request' : 'requests'} recorded. Example paths aren&apos;t exposed on this rollup version.
        </div>
      </div>
    );
  }
  return (
    <div style={{ padding: 16, border: `1px solid ${C.border}`, borderRadius: 2, background: C.surface }}>
      <Kicker>Probe paths</Kicker>
      <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, marginTop: -4, marginBottom: 10, lineHeight: 1.4 }}>
        Requests under this name for files no real agent asks for. Real {name} doesn&apos;t do this.
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: sansFont, fontSize: 12.5 }} data-testid="agent-detail-probe-paths">
          <thead>
            <tr style={{ fontFamily: monoFont, fontSize: 9, letterSpacing: 1.5, color: C.muted, textTransform: 'uppercase' }}>
              <th style={{ textAlign: 'left', padding: '6px 8px 8px 0', fontWeight: 500 }}>Path</th>
              <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 500 }}>Kind</th>
              <th style={{ textAlign: 'right', padding: '6px 0 8px 8px', fontWeight: 500 }}>Hits</th>
            </tr>
          </thead>
          <tbody>
            {probes.map((p) => (
              <tr key={p.path} style={{ borderTop: `1px solid ${C.borderSoft}` }}>
                <td style={{ padding: '8px 8px 8px 0', fontFamily: monoFont, color: C.text, wordBreak: 'break-all' }}>{p.path}</td>
                <td style={{ padding: '8px', color: C.body }}>{probeKindLocal(p.path)}</td>
                <td style={{ padding: '8px 0 8px 8px', textAlign: 'right', fontFamily: monoFont, color: C.text, fontVariantNumeric: 'tabular-nums' }}>{fmtInt(p.n)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Local alias of the Probes.tsx classifier so the Agent page doesn't
 *  pull in the full Probes module. */
function probeKindLocal(path: string): string {
  const p = path.toLowerCase();
  if (/\.(env|git|htaccess|htpasswd|yml|yaml|ini|aws|npmrc|dockercfg|bash_history|ssh|pem|key|crt|p12)/.test(p)) return 'Secret file';
  if (/\.php(\?|$|\.)/.test(p) || /\/(wp-login|xmlrpc|wp-admin|phpmyadmin)/.test(p)) return p.includes('wp-') ? 'WordPress login' : 'PHP file';
  if (/\/(admin|administrator|cgi-bin|server-status|server-info|debug|console|shell)/.test(p)) return 'Server internals';
  if (/(^|\/)\.\.(\/|$)/.test(p) || /(\.\.%2f|%2e%2e)/.test(p)) return 'Path escape';
  if (/^\/(graphql|graphiql)/.test(p)) return 'GraphQL';
  return 'Probe';
}

/** ADR-0235 — present only while the site's trust verdicts are live. */
export interface AgentDetailTrust {
  readonly verdict: MerchantAgentVerdict | null;
  readonly site_id: string;
  readonly canDispute: boolean;
  readonly windowPhrase: string;
  readonly onDisputed: (claim_key: string, dispute: MerchantAgentDisputeRef) => void;
}

export interface AgentDetailProps {
  readonly agent: AgentRollupSummary;
  readonly products: readonly AgentProductRollup[];
  readonly onBack: () => void;
  readonly onViewActivity: (name: string) => void;
  readonly trust?: AgentDetailTrust | null;
}

export function AgentDetail({ agent, products, onBack, onViewActivity, trust = null }: AgentDetailProps): JSX.Element {
  const siteCtx = useContext(SiteCtx);
  const C = useC();
  const name = agentDisplayName(agent);
  // Identity-status chip + "How we know" explainer. Pure classifier
  // shared with the Journey drawer so both surfaces say the same thing.
  const identity_status = agentIdentityStatus({
    has_known_bot_name:
      (agent.agent_id !== null && agent.agent_id.length > 0)
      || looksLikeKnownBotName(agent.inferred_name),
    signed_verified_hits: Number(agent.signed_verified_hits ?? 0),
    signature_invalid_hits: Number(agent.spoof_signals?.['sig_invalid'] ?? 0),
    cf_verified_hits: Number(agent.cf_verified_hits ?? 0),
    cf_unverified_hits: Number(agent.spoof_signals?.['cf_unverified'] ?? 0),
    typo_ua_hits: Number(agent.spoof_signals?.['typo_ua'] ?? 0),
    probe_hits: Number(agent.probe_hits ?? 0),
    impersonation_hits: impersonationHitsFromSignals(agent.spoof_signals),
  });
  const identity_label = agentIdentityStatusLabel(identity_status);
  const trust_verdict = trust?.verdict ?? null;
  // ADR-0235 — while trust verdicts are live and one matches, its label
  // drives "Who this is" so the page never says two different things.
  const identity_explainer = trust_verdict !== null
    ? trustLabelHeadline(trust_verdict.label, name)
    : agentIdentityStatusExplanation(identity_status, name);
  const show_impersonator = trust_verdict !== null
    ? trust_verdict.label === 'likely_impersonator'
    : identity_status === 'likely_impersonator';
  const journey = JOURNEY_ORDER.filter((k) => (agent.hits_by_kind[k] ?? 0) > 0).map((k) => [k, agent.hits_by_kind[k] ?? 0] as [string, number]);
  const kindTotal = sumValues(agent.hits_by_kind);
  const reachedCart = agent.hits_by_kind.cart ?? 0;
  const reachedCheckout = agent.hits_by_kind.checkout ?? 0;
  // Filter the server-side top_paths through the same probe classifier
  // used by the overview — defence in depth for merchants browsing a
  // stale cache / earlier rollup that pre-dated the server-side filter.
  // Platform-aware: on a WordPress site its own `.php` pages are not probes.
  const probeOpts = { wordpress: siteIsWordPress(siteCtx?.activeSite) };
  const cleanTopPaths = (agent.top_paths ?? []).filter((p) => !isProbePath(p.path, probeOpts));
  const probeHitsFromDetail = (agent.top_paths ?? [])
    .filter((p) => isProbePath(p.path, probeOpts))
    .reduce((s, p) => s + p.n, 0);
  const probeExamplesFromDetail = (agent.top_paths ?? [])
    .filter((p) => isProbePath(p.path, probeOpts))
    .slice(0, 5);
  // Prefer server-reported probe_hits when present (newer rollups);
  // fall back to the derived count when not (backwards-compat).
  const probeHits = agent.probe_hits ?? probeHitsFromDetail;
  const probeExamples = (agent.probe_examples && agent.probe_examples.length > 0)
    ? agent.probe_examples
    : probeExamplesFromDetail;
  const decisionEntries = sortedEntries(
    Object.fromEntries(agent.daily.reduce((m, d) => {
      m.set('approved', (m.get('approved') ?? 0) + d.approved);
      m.set('rejected', (m.get('rejected') ?? 0) + d.rejected);
      return m;
    }, new Map<string, number>())),
  ).filter(([, n]) => n > 0);

  return (
    <div data-testid="agent-detail">
      <button
        type="button"
        onClick={onBack}
        style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: monoFont, fontSize: 10, letterSpacing: 1.5, color: C.muted, marginBottom: 18 }}
      >
        <ArrowLeft size={12} /> ALL AGENTS
      </button>

      <div className="flex flex-wrap items-end justify-between gap-4" style={{ marginBottom: 20 }}>
        <div>
          <div className="flex items-center gap-2 flex-wrap" style={{ marginBottom: 8 }}>
            {agent.visitor_class !== 'human_likely' && agent.category !== null && agent.category !== 'human'
              ? <Pill tone="neutral">{CATEGORY_LABEL[agent.category] ?? agent.category}</Pill>
              : agent.visitor_class === 'human_likely' || agent.category === 'human'
                ? <Pill tone="neutral">Person</Pill>
                : null}
            {trust !== null && trust.verdict !== null ? (
              // ADR-0235 — the trust engine's label replaces the older
              // identity-status pill while verdicts are live for the site.
              <TrustChip
                verdict={trust.verdict}
                name={name}
                site_id={trust.site_id}
                canDispute={trust.canDispute}
                windowPhrase={trust.windowPhrase}
                onDisputed={trust.onDisputed}
              />
            ) : identity_label.length > 0 ? (
              <Tooltip
                content={identity_explainer}
                displayMode="inline-flex"
              >
                <Pill
                  tone={identity_status === 'likely_impersonator' ? 'coral' : 'neutral'}
                  data-testid="agent-detail-identity-status"
                >
                  {identity_label}
                </Pill>
              </Tooltip>
            ) : null}
          </div>
          <h2 style={{ fontFamily: serifFont, fontSize: 'clamp(30px, 4vw, 40px)', lineHeight: 1.05, color: C.text, letterSpacing: -1, margin: 0 }}>{name}</h2>
          <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 1, marginTop: 8 }}>
            {agent.visitor_class === 'human_likely' ? 'plain browsers' : (agent.agent_id ?? 'unregistered')}
            {agent.variants > 1 ? ` · ${fmtInt(agent.variants)} ${agent.visitor_class === 'human_likely' ? 'browser builds' : 'versions'} folded into this row` : ''}
            {' · '}first seen {fmtDay(agent.first_day)} · last seen {fmtDay(agent.last_day)}
          </div>
        </div>
        <button
          type="button"
          onClick={() => onViewActivity(name)}
          className="px-3 py-2"
          style={{ background: C.lime, color: C.ink, border: 'none', borderRadius: 2, fontFamily: sansFont, fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          View signed activity <ArrowRight size={12} />
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2" style={{ marginBottom: 16 }}>
        <Tile label="Visits" value={fmtInt(agent.visits)} />
        <Tile label="Page loads" value={fmtInt(agent.page_loads)} />
        <Tile label="Sessions" value={fmtInt(agent.sessions)} />
        <Tile label="Actions" value={fmtInt(agent.actions)} sub={agent.actions > 0 ? `${fmtPct(agent.approved, agent.approved + agent.rejected)} approved` : undefined} />
        <Tile label="Reached checkout" value={fmtInt(reachedCheckout)} sub={reachedCart > 0 ? `${fmtInt(reachedCart)} reached cart` : undefined} />
        <Tile label="Revenue" value={fmtMoney(agent.revenue_minor, agent.currency)} sub={agent.refunded_minor > 0 ? `${fmtMoney(agent.refunded_minor, agent.currency)} refunded` : undefined} />
      </div>

      {/* "Who this is" — full-width, always open. Impersonator variant
          is coral-bordered and leads with the probe behaviour. */}
      <div
        data-testid="agent-detail-who-this-is"
        style={{
          padding: 16,
          border: `1px solid ${show_impersonator ? C.coralFg : C.border}`,
          background: C.surface,
          borderRadius: 2,
          marginBottom: 12,
        }}
      >
        <Kicker>Who this is</Kicker>
        {show_impersonator && trust_verdict !== null ? (
          <div style={{ fontFamily: sansFont, fontSize: 13.5, color: C.body, lineHeight: 1.55 }} data-testid="agent-detail-trust-impersonator">
            {identity_explainer} Select <strong style={{ color: C.text, fontWeight: 500 }}>Why?</strong> next to the label for the evidence.
            Block the impostor&apos;s addresses from any visit under this name &mdash; the real {name} keeps working.
          </div>
        ) : show_impersonator ? (
          <div style={{ fontFamily: sansFont, fontSize: 13.5, color: C.body, lineHeight: 1.55 }}>
            Someone is using <strong style={{ color: C.text, fontWeight: 500 }}>{name}&apos;s</strong> name.
            Requests under this name tried to read secret or admin files {fmtInt(probeHits)} {probeHits === 1 ? 'time' : 'times'}.
            Block the impostor&apos;s addresses from any visit under this name &mdash; the real {name} keeps working.
            {probeHits > 0 ? (
              <>
                {' '}
                <a href="#probe-paths" style={{ color: C.coralFg, textDecoration: 'underline' }}>See probe paths ↓</a>
              </>
            ) : null}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {identity_explainer.length > 0 ? (
              <div
                data-testid="agent-detail-identity-explainer"
                style={{ fontFamily: sansFont, fontSize: 13.5, color: C.body, lineHeight: 1.55 }}
              >
                <span style={{ fontFamily: monoFont, fontSize: 9, letterSpacing: 1.5, color: C.muted, textTransform: 'uppercase', marginRight: 6 }}>
                  How we know
                </span>
                {identity_explainer}
              </div>
            ) : null}
            {agent.verdict !== null ? (
              <div style={{ fontFamily: sansFont, fontSize: 12.5, color: C.muted, lineHeight: 1.55 }}>
                <span style={{ fontFamily: monoFont, fontSize: 9, letterSpacing: 1.5, color: C.muted, textTransform: 'uppercase', marginRight: 6 }}>
                  Recommendation
                </span>
                {agent.verdict === 'safe' ? 'no action'
                  : agent.verdict === 'monitor' ? 'watch'
                  : agent.verdict === 'block' ? 'consider a guardrail'
                  : VERDICT_LABEL[agent.verdict] ?? agent.verdict}
              </div>
            ) : null}
          </div>
        )}
      </div>

      <div style={{ padding: 16, border: `1px solid ${C.border}`, borderRadius: 2, background: C.surface, marginBottom: 12 }}>
        <div className="flex items-center justify-between" style={{ marginBottom: 6 }}>
          <Kicker>Daily traffic</Kicker>
        </div>
        <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, marginTop: -4, marginBottom: 10 }}>
          Bars are page loads; the solid part is requests to act.
        </div>
        <TrendChart daily={agent.daily} />
      </div>

      {(() => {
        const has_paths = cleanTopPaths.length > 0;
        const has_terms = agent.search_terms.length > 0;
        const has_skills = sortedEntries(agent.actions_by_skill).length > 0;
        const has_decisions = decisionEntries.length > 0;
        const has_where = sortedEntries(agent.protocol).length > 0 || sortedEntries(agent.geo).length > 0;
        // Empty panels are noise on a page about one visitor; say in one
        // line what has not happened.
        const quiet: string[] = [];
        if (!has_terms) quiet.push('No searches');
        if (!has_skills) quiet.push('actions');
        if (!has_decisions) quiet.push('decisions yet.');
        const everything_quiet = !has_terms && !has_skills && !has_decisions;
        return (
          <>
            {/* 5. Top pages — EXPANDED always, 10 rows + "Show all N" */}
            {has_paths ? (
              <div style={{ marginBottom: 12 }}>
                <TopPagesExpanded paths={cleanTopPaths} />
              </div>
            ) : null}

            {/* 6. Probe paths (only when probe_hits > 0), expanded */}
            {probeHits > 0 ? (
              <div id="probe-paths" style={{ marginBottom: 12 }}>
                <ProbePathsSection probes={probeExamples} name={name} total={probeHits} />
              </div>
            ) : null}

            {everything_quiet ? (
              <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, marginBottom: 12 }}>
                No searches, actions or decisions yet.
              </div>
            ) : null}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* 7. Pages loaded by kind */}
              <Section
                title="Pages loaded by kind"
                help="The same page loads, grouped into product, collection, cart and checkout pages."
              >
                {journey.length === 0
                  ? <div style={{ fontFamily: sansFont, fontSize: 12, color: C.dim }}>No page loads recorded yet — browser sessions are kept from 2026-09-22 forward; server-side fetchers show as traffic only.</div>
                  : <CountList entries={journey} labels={PAGE_KIND_LABEL} total={kindTotal} />}
              </Section>

              {/* 8. Searches — expanded */}
              {has_terms ? (
                <Section
                  title="Searches"
                  help="What it typed into your site search, or asked for through browse and recommend."
                >
                  <CountList entries={agent.search_terms.map((t) => [t.term, t.n])} />
                </Section>
              ) : null}

              {/* 9. Skills called */}
              {has_skills ? (
                <Section
                  title="Skills called"
                  help="Actions it asked Sill to perform, such as checking stock or placing an order."
                >
                  <CountList entries={sortedEntries(agent.actions_by_skill)} />
                </Section>
              ) : null}

              {/* 10. Decisions */}
              {has_decisions ? (
                <Section
                  title="Decisions"
                  help="What your guardrails decided on those requests."
                >
                  <CountList entries={decisionEntries} labels={DECISION_LABEL} />
                  {sortedEntries(agent.reject_reasons).length > 0 ? (
                    <div style={{ marginTop: 12 }}>
                      <Kicker>Why requests were refused</Kicker>
                      <CountList entries={sortedEntries(agent.reject_reasons)} />
                    </div>
                  ) : null}
                </Section>
              ) : null}

              {/* 11. How it reached you */}
              {has_where ? (
                <Section
                  title="How it reached you"
                  help="Which Sill install saw it, and the country of the request."
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <div style={{ fontFamily: monoFont, fontSize: 9, letterSpacing: 1.5, color: C.dim, marginBottom: 6 }}>HOW IT GOT HERE</div>
                      <CountList entries={sortedEntries(agent.protocol)} labels={PROTOCOL_LABEL} />
                    </div>
                    <div>
                      <div style={{ fontFamily: monoFont, fontSize: 9, letterSpacing: 1.5, color: C.dim, marginBottom: 6 }}>COUNTRY</div>
                      <CountList
                        entries={sortedEntries(agent.geo)}
                        renderLabel={(k) => <CountryFlag code={k} withName />}
                      />
                    </div>
                  </div>
                </Section>
              ) : null}
            </div>
          </>
        );
      })()}

      {products.length > 0 ? (
        <div style={{ marginTop: 12 }}>
          <Section title="Products this agent touched">
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: sansFont, fontSize: 12.5 }}>
                <thead>
                  <tr style={{ fontFamily: monoFont, fontSize: 9, letterSpacing: 1.5, color: C.muted, textTransform: 'uppercase' }}>
                    <th style={{ textAlign: 'left', padding: '6px 8px 8px 0', fontWeight: 500 }}>Product</th>
                    <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 500 }}>Views</th>
                    <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 500 }}>Requested</th>
                    <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 500 }}>Approved</th>
                    <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 500 }}>Refused</th>
                    <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 500 }}>Unavailable</th>
                    <th style={{ textAlign: 'right', padding: '6px 0 8px 8px', fontWeight: 500 }}>Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p) => (
                    <tr key={p.catalog_product_id} style={{ borderTop: `1px solid ${C.borderSoft}` }}>
                      <td style={{ padding: '8px 8px 8px 0', color: C.text }}>{p.title ?? p.catalog_product_id}</td>
                      <td style={{ padding: '8px', textAlign: 'right', fontFamily: monoFont, color: C.text }}>{fmtInt(p.views)}</td>
                      <td style={{ padding: '8px', textAlign: 'right', fontFamily: monoFont, color: C.text }}>{fmtInt(p.requested_orders)}</td>
                      <td style={{ padding: '8px', textAlign: 'right', fontFamily: monoFont, color: C.text }}>{fmtInt(p.approved_orders)}</td>
                      <td style={{ padding: '8px', textAlign: 'right', fontFamily: monoFont, color: p.rejected_orders > 0 ? C.coralFg : C.muted }}>{fmtInt(p.rejected_orders)}</td>
                      <td style={{ padding: '8px', textAlign: 'right', fontFamily: monoFont, color: p.unavailable_when_requested > 0 ? C.amberFg : C.muted }}>{fmtInt(p.unavailable_when_requested)}</td>
                      <td style={{ padding: '8px 0 8px 8px', textAlign: 'right', fontFamily: monoFont, color: C.text }}>{fmtMoney(p.revenue_minor, p.currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
        </div>
      ) : null}
    </div>
  );
}
